<template>
  <div ref="root">
    <div
      class="mb-8 flex flex-wrap justify-between gap-8 md:flex-row md:flex-nowrap md:gap-6"
    >
      <button
        type="button"
        class="active all-filter border-b border-black"
        @click="clear()"
      >
        Alle
      </button>

      <div class="basis-full md:basis-1/3">
        <label class="sr-only" for="team-filter-name">
          Ansprechsperson suchen
        </label>
        <input
          id="team-filter-name"
          name="teamFilterName"
          type="text"
          v-model="searchName"
          @input="filter()"
          placeholder="Ansprechsperson suchen ..."
          class="form-input w-full"
        />
      </div>
      <div class="basis-full md:basis-1/3">
        <label class="sr-only" for="team-filter-location">Niederlassung</label>
        <select
          id="team-filter-location"
          name="teamFilterLocation"
          v-model="selectedLocation"
          @change="filter()"
          class="form-select w-full"
        >
          <option :value="0">Alle Niederlassungen</option>
          <option
            v-for="location in locations"
            :key="location.id"
            :value="location.id"
          >
            {{ location.title }}
          </option>
        </select>
      </div>
      <div class="basis-full md:basis-1/3">
        <label class="sr-only" for="team-filter-field">Fachbereich</label>
        <select
          id="team-filter-field"
          name="teamFilterField"
          v-model="selectedField"
          @change="filter()"
          class="form-select w-full"
        >
          <option :value="0">Alle Fachbereiche</option>
          <option v-for="field in fields" :key="field.id" :value="field.id">
            {{ field.title }}
          </option>
        </select>
      </div>
    </div>
    <slot></slot>
  </div>
</template>

<script lang="ts" setup>
import { ref, onMounted, nextTick } from 'vue';
import { trackEvent } from '../utils/tracking';

defineProps<{
  locations: Array<any>;
  fields: Array<any>;
}>();

const root = ref<HTMLElement | null>(null);
const selectedLocation = ref(0);
const selectedField = ref(0);
const searchName = ref('');
let teamCards: HTMLElement[] = [];

// Debounced so a burst of keystrokes in the name field collapses into a single
// Matomo event reporting which filter dimensions are active.
let trackTimeout: ReturnType<typeof setTimeout>;
const trackFilter = () => {
  clearTimeout(trackTimeout);
  trackTimeout = setTimeout(() => {
    const parts: string[] = [];
    if (searchName.value.trim()) parts.push('name');
    if (selectedLocation.value) parts.push('location');
    if (selectedField.value) parts.push('field');
    if (parts.length) trackEvent('Team Filter', 'Apply', parts.join('+'));
  }, 500);
};

const filter = (removeActive: boolean = true) => {
  if (removeActive) {
    root.value
      ?.querySelectorAll('.all-filter')
      .forEach((el) => el.classList.remove('active'));
  }

  teamCards.forEach((card) => {
    const locations = (card.dataset.location || '')
      .split(',')
      .map((l) => Number.parseInt(l.trim()));
    const fields = (card.dataset.fields || '')
      .split(',')
      .map((f) => Number.parseInt(f.trim()));
    const name = `${card.dataset.firstname} ${card.dataset.lastname}`;

    const locationMatch =
      !selectedLocation.value || locations.includes(selectedLocation.value);
    const fieldMatch =
      !selectedField.value || fields.includes(selectedField.value);
    const nameMatch =
      !searchName.value || name.includes(searchName.value.toLowerCase());

    if (locationMatch && fieldMatch && nameMatch) {
      card.style.display = 'block';
    } else {
      card.style.display = 'none';
    }
  });

  trackFilter();
};

const clear = () => {
  selectedLocation.value = 0;
  selectedField.value = 0;
  searchName.value = '';

  root.value
    ?.querySelectorAll('.all-filter')
    .forEach((el) => el.classList.add('active'));

  filter(false);
};

onMounted(() => {
  nextTick(() => {
    if (root.value) {
      teamCards = Array.from(root.value.querySelectorAll('.team-card'));
    }
  });
});
</script>

<style lang="scss" scoped>
select {
  @apply border-0 p-0 focus:outline-none focus:ring-0;
  background-position: right center;
}
input {
  @apply border-0 p-0 focus:outline-none focus:ring-0;
}

.form-select,
.form-input {
  @apply border-b focus:border-b-2 focus:border-primary;
}

.all-filter {
  @apply cursor-pointer;

  &.active {
    @apply border-b-2 border-primary font-extrabold text-primary;
  }
}
</style>
